import React, {useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import {LoadingOutlined, MenuOutlined} from "@ant-design/icons";
import {Checkbox, Spin, Progress } from "antd"
import { observer } from "mobx-react-lite";
import { storeMassAddition } from "@/store/massAddition";

import {File} from '@/types/Files'
import type {MassAdditionCreateForm} from "@/types/MassAddition";

import CardContent from "@/components/card/CardContent";

import './styles/mass-addition-list-control.scss'

interface Props {
    list: File[],
    form: MassAdditionCreateForm,

    update: (files:File[]) => void
}

const MassAdditionListControl = observer(forwardRef<HTMLDivElement, Props>(({ list, form, update }, ref) => {
    const [rows, setRows] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [progressProcent, setProgressProcent] = useState<number>(10);

    // Уже добавленные файлы выбрать нельзя, поэтому «отметить все» считается
    // только по доступным — иначе шапка показывала бы неверное состояние.
    const selectableRows = rows.filter((row) => !row.is_added);
    const isCheckedAll = selectableRows.length > 0 && selectableRows.every((row) => row.isChecked);

    const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
        e.dataTransfer.setData('text/plain', index.toString());
        e.currentTarget.classList.add('dragging');
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, targetIndex: number) => {
        e.preventDefault();
        const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

        if (draggedIndex === targetIndex) return;

        const newRows = [...rows];
        const draggedItem = newRows[draggedIndex];
        newRows.splice(draggedIndex, 1);
        newRows.splice(targetIndex, 0, draggedItem);

        // Обновляем порядок в каждом элементе
        const updatedRows = newRows.map((item, index) => ({
            ...item,
            orderBy: form.startNumber ? Number(form.startNumber) + index : index + 1
        }));

        setRows(updatedRows);
        document.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
    };

    const handleDragEnd = () => {
        document.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
    };

    const onChangeAll = (val:boolean)=> {
        setRows((prev) => prev.map((file) => ({
            ...file,
            // уже добавленные остаются снятыми: их всё равно отклонит бэкенд
            isChecked: file.is_added ? false : Boolean(val),
        })))
    }

    const formattedList = () => {
        let index = 0;
        const formattedList = list.map((file) => {

            let orderBy;
            if (form.startNumber !== undefined && form.startNumber !== null) {
                // Если form.startNumber существует и не является пустой строкой (но 0 считается валидным значением)
                orderBy = Number(form.startNumber) + index;
            } else if (index === 0) {
                orderBy = 1;
            } else {
                orderBy = index + 1;
            }

            file = {
                ...file,
                // уже добавленный файл не отмечаем — выбрать его нельзя
                isChecked: !file.is_added,
                // orderBy: form.startNumber ? Number(form.startNumber) + index : index === 0 ? 1 : index + 1
                orderBy: orderBy
            }

            index++;

            return file
        })

        setRows(formattedList);
    }

    const onChangeItem = (index: number, checked: boolean) => {
        setRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                isChecked: checked
            };

            return newRows;
        });
    };

    const showLoader = (type) => {
        if(type === 'stop') {
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
    }

    useImperativeHandle(ref, () => ({
        showLoader,
    }));

    useEffect(() => {
        formattedList()
    }, [list]);

    useEffect(() => {
        update(rows)
    }, [rows])

    return (
        <div className={'mass-addition-accept'}>
            <CardContent title={'Список для добавления'}>
                {
                    isLoading && <div className={'mass-addition-form-create-loading'}>
                        <Spin indicator={<LoadingOutlined />} className={'mass-addition-form-create-loader'} />
                        <Progress percent={storeMassAddition.progressPercent} />
                    </div>
                }

                {
                    !isLoading && <div className={"mass-addition-accept__container"}>
                        <div className={'mass-addition-accept__texts'}>
                            <p>Перетащите элементы для изменения порядка</p>
                            <p>Снимите галочку, чтобы исключить файл из импорта</p>
                        </div>

                        <table className={"mass-addition-accept__table"}>
                            <thead>
                            <tr>
                                <th>
                                    <Checkbox onChange={(event) => onChangeAll(event.target.checked)} checked={isCheckedAll} />
                                </th>
                                <th></th>
                                <th>Порядок</th>
                                <th>Название</th>
                                <th>Размер</th>
                            </tr>
                            </thead>

                            <tbody>
                            {
                                rows.map((item, index) => (
                                    <tr
                                        key={index}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, index)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <td>
                                            <Checkbox
                                                checked={item.isChecked}
                                                disabled={item.is_added}
                                                onChange={(e) => onChangeItem(index, e.target.checked)}
                                            />
                                        </td>
                                        <td>
                                            <MenuOutlined/>
                                        </td>
                                        <td>
                                            {item.orderBy}
                                        </td>
                                        <td>
                                            {item.filename}
                                            {item.is_added && (
                                                <span
                                                    className={'mass-addition-accept__added'}
                                                    title={item.added_to || 'Файл уже добавлен'}
                                                >
                                                    уже добавлен
                                                </span>
                                            )}
                                        </td>
                                        <td>{item.size_formatted}</td>
                                    </tr>
                                ))
                            }
                            </tbody>
                        </table>
                    </div>
                }
            </CardContent>
        </div>
    );
}));

export default MassAdditionListControl;